/**
 * FindingStatusIndicator — Comprehensive Documentation & Demo
 *
 * This file documents the FindingStatusIndicator component, which renders a
 * colored status chip with an icon and label. Clicking the chip opens a
 * context menu whose items vary depending on the finding's lifecycle state.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ARCHITECTURE
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Files:
 *   src/components/FindingStatusIndicator/
 *   ├── index.ts                  Re-exports public API
 *   ├── FindingStatusIndicator.tsx The React component (chip + menu)
 *   ├── findingStatusConfig.ts    Pure config: status→visual mapping & menu logic
 *   └── FindingStatusIndicator.spec.tsx  Unit + component tests
 *
 * Key exports:
 *   - FindingStatusIndicator    — the component
 *   - getStatusConfig(state)    — returns { label, icon, backgroundColor, color }
 *   - getMenuItems(state, actionState, callbacks) — returns MenuItemDef[]
 *   - FindingStatusConfig       — type for the visual config
 *   - MenuCallbacks             — type for the callback props
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * PROPS
 * ═══════════════════════════════════════════════════════════════════════════
 *
 *  | Prop           | Type       | Required | Description                          |
 *  |----------------|------------|----------|--------------------------------------|
 *  | overallState   | string     | yes      | The finding's lifecycle state.        |
 *  | actionState    | string     | no       | Sub-state that controls fix actions.  |
 *  | findingName    | string     | no       | Shown as header text in the menu.     |
 *  | onFixFinding   | () => void | no       | Called when "Fix finding" is clicked. |
 *  | onCreateTicket | () => void | no       | Called when "Create ticket" clicked.  |
 *  | onSetException | () => void | no       | Called when "Set exception" clicked.  |
 *  | onViewFix      | () => void | no       | Called when "View fix" is clicked.    |
 *  | onViewTicket   | () => void | no       | Called when "View ticket" clicked.    |
 *  | onViewException| () => void | no       | Called when "View exception" clicked. |
 *  | onCopyLink     | () => void | no       | Called when "Copy link" is clicked.   |
 *  | onViewDetails  | () => void | no       | Called when "View details" clicked.   |
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * STATUS CATEGORIES & VISUAL STYLES
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Statuses are grouped into visual categories, each with a distinct color:
 *
 *  Category         | States                              | Background          | Text
 *  -----------------|-------------------------------------|---------------------|--------
 *  Urgent           | NEW, REGRESSED                      | critical.main (red) | white
 *  Action Required  | DETECTED, FIX_AVAILABLE             | action.hover        | text.primary
 *  Actioned         | TICKETED, IN_PROGRESS, PR_CREATED,  | inverse canvas      | inverse text
 *                   | APPROVED, DEPLOYED                  | (dark↔light swap)   |
 *  Exception        | WORKAROUND, FALSE_POSITIVE,         | action.hover        | text.secondary
 *                   | RISK_ACCEPTED, DISABLED             |                     |
 *  Fixed            | FIXED                               | none.main (green)   | white
 *  Neutral          | UNAFFECTED                          | action.hover        | text.primary
 *  Fallback         | (any unknown value)                 | action.hover        | text.secondary
 *
 * Each status also has a unique icon (see findingStatusConfig.ts for mappings).
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * MENU LOGIC (getMenuItems)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * The menu items returned depend on overallState and actionState:
 *
 * 1. Exception states (WORKAROUND, FALSE_POSITIVE, RISK_ACCEPTED, DISABLED):
 *    → View exception | --- | Copy link, View details
 *
 * 2. FIXED:
 *    → View fix | --- | Copy link, View details
 *
 * 3. TICKETED or IN_PROGRESS:
 *    → Fix in progress (disabled), View ticket, Set exception | --- | Copy link, View details
 *
 * 4. FIX_AVAILABLE:
 *    → Fix finding, Create ticket, Set exception | --- | Copy link, View details
 *
 * 5. Active fix states (NEW, REGRESSED, DETECTED) with script action
 *    (actionState = "SCRIPT" or "SECURITY_PIPELINE"):
 *    → Fix finding, Create ticket, Set exception | --- | Copy link, View details
 *
 * 6. Active fix states without script action (actionState is empty/other):
 *    → Fix unavailable (disabled), Create ticket, Set exception | --- | Copy link, View details
 *
 * 7. Fallback (any other state, e.g. UNAFFECTED or unknown):
 *    → Copy link, View details
 *
 * Note: "---" represents a visual divider before "Copy link" when it is not
 *       the first item in the menu.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * USAGE EXAMPLE
 * ═══════════════════════════════════════════════════════════════════════════
 *
 *   import { FindingStatusIndicator } from "~/components/FindingStatusIndicator";
 *
 *   <FindingStatusIndicator
 *     overallState="NEW"
 *     actionState="SCRIPT"
 *     findingName="CVE-2024-12345 — Critical RCE in libfoo"
 *     onFixFinding={() => openFixDialog(finding)}
 *     onCreateTicket={() => openTicketForm(finding)}
 *     onSetException={() => openExceptionDialog(finding)}
 *     onCopyLink={() => copyToClipboard(findingUrl)}
 *     onViewDetails={() => navigate(`/findings/${finding.id}`)}
 *   />
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * EXTENDING THE COMPONENT
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * To add a new status:
 *   1. Add an entry to `statusConfigMap` in findingStatusConfig.ts
 *      with { label, icon, backgroundColor, color }.
 *   2. If the new status needs custom menu items, add a branch in
 *      `getMenuItems()` before the fallback return.
 *   3. If the status belongs to an existing group (e.g. exceptions),
 *      add it to the relevant Set (EXCEPTION_STATES, ACTIVE_FIX_STATES, etc).
 *   4. Add the new status to the tests and to the demo list below.
 *
 * The config uses `Partial<Record<string, ...>>` so unknown states from the
 * API won't break the build — they'll just render the fallback ("Unknown").
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * DEMO
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Drop this component into a route or render it in dev to see every chip
 * variant and menu variant with fake data. All actions are logged to the
 * console output panel on the right side of the page.
 */

import { useCallback, useRef, useState } from "react";
import { Box, Typography, Divider, IconButton } from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { FindingStatusIndicator } from "~/components/FindingStatusIndicator";
import {
  FindingActionState,
  FindingAgeState,
  FindingEnforcementState,
  FindingExceptionState,
  FindingOverallState,
  FindingScoreState,
  FindingSecurityPipelineState,
  FindingTicketState,
  type FindingState,
} from "~/operations";

type LogEntry = { id: number; message: string; timestamp: string };

type Demo = {
  states: FindingState;
  label: string;
  description: string;
};

function makeStates(
  overrides: Partial<FindingState> & { overall: FindingOverallState },
): FindingState {
  return {
    __typename: "FindingState",
    score: FindingScoreState.Failed,
    age: FindingAgeState.None,
    action: FindingActionState.Unknown,
    securityPipeline: FindingSecurityPipelineState.None,
    ticket: FindingTicketState.None,
    exception: FindingExceptionState.None,
    enforcement: FindingEnforcementState.Enforcing,
    ...overrides,
  };
}

// Every known status with the states that trigger its distinct menu variant
const demos: Demo[] = [
  // --- Urgent (critical background) ---
  {
    states: makeStates({ overall: FindingOverallState.New }),
    label: "New — no fix available",
    description:
      'Menu shows "Fix unavailable" (disabled), Create ticket, Set exception',
  },
  {
    states: makeStates({
      overall: FindingOverallState.New,
      action: FindingActionState.Script,
    }),
    label: "New — fix available (SCRIPT)",
    description:
      'actionState="SCRIPT" enables the "Fix finding" action in the menu',
  },
  {
    states: makeStates({
      overall: FindingOverallState.New,
      action: FindingActionState.SecurityPipeline,
    }),
    label: "New — fix available (SECURITY_PIPELINE)",
    description: 'actionState="SECURITY_PIPELINE" also enables "Fix finding"',
  },
  {
    states: makeStates({ overall: FindingOverallState.Regressed }),
    label: "Regressed",
    description: "Same menu behavior as NEW — uses critical/red background",
  },

  // --- Action Required (neutral background) ---
  {
    states: makeStates({ overall: FindingOverallState.Detected }),
    label: "Detected",
    description:
      "Active fix state without script action — shows Fix unavailable (disabled)",
  },
  {
    states: makeStates({
      overall: FindingOverallState.Detected,
      ticket: FindingTicketState.Ticketed,
    }),
    label: "Detected — with ticket",
    description:
      'ticket=TICKETED makes the menu show "View ticket" instead of "Create ticket"',
  },
  {
    states: makeStates({ overall: FindingOverallState.FixAvailable }),
    label: "Fix available",
    description:
      'Dedicated branch — always shows "Fix finding" enabled regardless of actionState',
  },

  // --- Actioned (inverse canvas — dark bg in light mode, light bg in dark) ---
  {
    states: makeStates({ overall: FindingOverallState.Ticketed }),
    label: "Ticketed",
    description:
      'Shows "Fix in progress" (disabled), View ticket, Set exception',
  },
  {
    states: makeStates({ overall: FindingOverallState.InProgress }),
    label: "In progress",
    description: "Same menu as TICKETED — uses a 50% progress icon",
  },
  {
    states: makeStates({
      overall: "PR_CREATED" as FindingOverallState,
    }),
    label: "PR created (future)",
    description: "Falls through to default menu (Copy link, View details only)",
  },
  {
    states: makeStates({
      overall: "APPROVED" as FindingOverallState,
    }),
    label: "Approved (future)",
    description: "Falls through to default menu — uses CheckCircle icon",
  },
  {
    states: makeStates({
      overall: "DEPLOYED" as FindingOverallState,
    }),
    label: "Deployed (future)",
    description: "Falls through to default menu — uses 75% progress icon",
  },

  // --- Exception (muted text on hover background) ---
  {
    states: makeStates({ overall: FindingOverallState.Workaround }),
    label: "Workaround",
    description: 'Exception menu — shows "View exception"',
  },
  {
    states: makeStates({ overall: FindingOverallState.FalsePositive }),
    label: "False positive",
    description: 'Exception menu — shows "View exception"',
  },
  {
    states: makeStates({ overall: FindingOverallState.RiskAccepted }),
    label: "Risk accepted",
    description: 'Exception menu — shows "View exception"',
  },
  {
    states: makeStates({ overall: FindingOverallState.Disabled }),
    label: "Disabled",
    description: 'Exception menu — shows "View exception"',
  },

  // --- Fixed (green background) ---
  {
    states: makeStates({ overall: FindingOverallState.Fixed }),
    label: "Fixed",
    description: 'Shows "View fix" as the primary action',
  },

  // --- Neutral ---
  {
    states: makeStates({
      overall: "UNAFFECTED" as FindingOverallState,
    }),
    label: "Unaffected (future)",
    description: "Falls through to default menu (Copy link, View details only)",
  },

  // --- Fallback ---
  {
    states: makeStates({
      overall: "SOMETHING_UNKNOWN" as FindingOverallState,
    }),
    label: "Unknown / fallback",
    description:
      'Any unrecognized state renders "Unknown" with HelpOutline icon and default menu',
  },
];

export function StatusTagsExample() {
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const idRef = useRef(0);
  const logContainerRef = useRef<HTMLDivElement>(null);

  const addLog = useCallback((action: string) => {
    setLogs((prev) => {
      const entry: LogEntry = {
        id: ++idRef.current,
        message: action,
        timestamp: new Date().toLocaleTimeString(),
      };
      return [...prev, entry];
    });
    setTimeout(() => {
      const el = logContainerRef.current;
      if (el) el.scrollTop = el.scrollHeight;
    });
  }, []);

  const clearLogs = () => setLogs([]);

  const log = (action: string) => () => addLog(action);

  const sharedCallbacks = {
    onFixFinding: log("Fix finding"),
    onCreateTicket: log("Create ticket"),
    onSetException: log("Set exception"),
    onViewFix: log("View fix"),
    onViewTicket: log("View ticket"),
    onViewException: log("View exception"),
    onCopyLink: log("Copy link"),
    onViewDetails: log("View details"),
  };

  return (
    <Box sx={{ display: "flex", gap: 3, p: 4, minHeight: "100vh" }}>
      {/* Left: Demo chips */}
      <Box sx={{ flex: 1, minWidth: 0, maxWidth: 640 }}>
        <Typography variant="h5" sx={{ mb: 1 }}>
          FindingStatusIndicator Demo
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Click any chip to open its context menu. Actions are logged to the
          console panel on the right.
        </Typography>

        <Divider sx={{ mb: 3 }} />

        {/* Category: Urgent */}
        <SectionHeader
          title="Urgent"
          subtitle="Critical findings requiring immediate attention"
        />
        <DemoGroup
          demos={demos.filter((d) =>
            ["NEW", "REGRESSED"].includes(d.states.overall),
          )}
          callbacks={sharedCallbacks}
        />

        {/* Category: Action Required */}
        <SectionHeader
          title="Action Required"
          subtitle="Findings detected but not yet acted on"
        />
        <DemoGroup
          demos={demos.filter((d) =>
            ["DETECTED", "FIX_AVAILABLE"].includes(d.states.overall),
          )}
          callbacks={sharedCallbacks}
        />

        {/* Category: Actioned */}
        <SectionHeader
          title="Actioned"
          subtitle="Findings with active remediation in progress"
        />
        <DemoGroup
          demos={demos.filter((d) =>
            [
              "TICKETED",
              "IN_PROGRESS",
              "PR_CREATED",
              "APPROVED",
              "DEPLOYED",
            ].includes(d.states.overall),
          )}
          callbacks={sharedCallbacks}
        />

        {/* Category: Exception */}
        <SectionHeader
          title="Exception"
          subtitle="Findings dismissed or accepted"
        />
        <DemoGroup
          demos={demos.filter((d) =>
            [
              "WORKAROUND",
              "FALSE_POSITIVE",
              "RISK_ACCEPTED",
              "DISABLED",
            ].includes(d.states.overall),
          )}
          callbacks={sharedCallbacks}
        />

        {/* Category: Fixed */}
        <SectionHeader
          title="Fixed"
          subtitle="Successfully remediated findings"
        />
        <DemoGroup
          demos={demos.filter((d) => d.states.overall === "FIXED")}
          callbacks={sharedCallbacks}
        />

        {/* Category: Neutral & Fallback */}
        <SectionHeader
          title="Neutral & Fallback"
          subtitle="Unaffected or unrecognized states"
        />
        <DemoGroup
          demos={demos.filter((d) =>
            ["UNAFFECTED", "SOMETHING_UNKNOWN"].includes(d.states.overall),
          )}
          callbacks={sharedCallbacks}
        />
      </Box>

      {/* Right: Console output */}
      <Box
        sx={{
          width: 340,
          flexShrink: 0,
          position: "sticky",
          top: 224,
          alignSelf: "flex-start",
          display: "flex",
          flexDirection: "column",
          height: 300,
          borderRadius: 2,
          border: 1,
          borderColor: "divider",
          overflow: "hidden",
        }}
      >
        {/* Console header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 2,
            py: 1,
            borderBottom: 1,
            borderColor: "divider",
            bgcolor: "action.hover",
          }}
        >
          <Typography
            variant="caption"
            sx={{ fontWeight: 700, letterSpacing: 0.5 }}
          >
            CONSOLE OUTPUT
          </Typography>
          <IconButton size="small" onClick={clearLogs} title="Clear console">
            <DeleteOutlineIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>

        {/* Console body */}
        <Box
          ref={logContainerRef}
          sx={{
            flex: 1,
            overflow: "auto",
            bgcolor: "background.default",
            fontFamily: "monospace",
            fontSize: 12,
            p: 1.5,
          }}
        >
          {logs.length === 0 && (
            <Typography
              variant="caption"
              color="text.disabled"
              sx={{ fontFamily: "monospace" }}
            >
              Click a menu action to see output here...
            </Typography>
          )}
          {logs.map((entry) => (
            <Box
              key={entry.id}
              sx={{
                display: "flex",
                gap: 1,
                py: 0.25,
                "&:not(:last-child)": {
                  borderBottom: "1px solid",
                  borderColor: "divider",
                },
              }}
            >
              <Typography
                component="span"
                sx={{
                  fontFamily: "monospace",
                  fontSize: 11,
                  color: "text.disabled",
                  flexShrink: 0,
                }}
              >
                {entry.timestamp}
              </Typography>
              <Typography
                component="span"
                sx={{ fontFamily: "monospace", fontSize: 12 }}
              >
                {entry.message}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

function SectionHeader({
  title,
  subtitle,
}: {
  title: string;
  subtitle: string;
}) {
  return (
    <Box sx={{ mt: 3, mb: 1.5 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
        {title}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {subtitle}
      </Typography>
    </Box>
  );
}

function DemoGroup({
  demos,
  callbacks,
}: {
  demos: Demo[];
  callbacks: Record<string, () => void>;
}) {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mb: 2 }}>
      {demos.map((demo) => (
        <Box
          key={`${demo.states.overall}-${demo.states.action}-${demo.states.ticket}`}
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: 2,
          }}
        >
          <Box sx={{ flexShrink: 0, pt: "2px" }}>
            <FindingStatusIndicator
              states={demo.states}
              findingName="CVE-2024-12345 — Example Finding Name"
              {...callbacks}
            />
          </Box>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {demo.label}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {demo.description}
            </Typography>
          </Box>
        </Box>
      ))}
    </Box>
  );
}
